import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { filter, take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import {  ClearRuntimeCacheMessage, RuntimeCachePayload } from '../../service-worker/clear-runtime-cache-message.model';
import { ServiceWorkerMessageService } from '../../service-worker/service-worker-message.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../../shared/instrumentation/instrumented-http.service';
import { BulkLicensePreviewData, BulkLicensePreviewRequestBody, LicenseItem, LicenseStatus, LicenseValidityType, ProductSkuIdContractV8, UserIdentityContractV8 } from '../../shared/models/bulk-license-preview.model';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { ObservableScope } from '../../shared/models/observable-scope.enum';
import { OperationStream } from '../../shared/models/operation-stream.model';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { ConsumerLicenseService, ConsumerSkuType, UserLicenses } from '../../shared/services/consumer-license.service';
import { EventService } from '../../shared/services/event.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { SchedulerService } from '../../shared/services/scheduler.service';
import { UniversalStoreConfig, UniversalStoreConfigService } from '../../shared/services/universal-store-config.service';
import { XhrService, XhrServiceError } from '../../shared/services/xhr.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesConfig, CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfig, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { ObservableHelper } from '../../shared/utilities/observable-helper.utility';

interface LightLicenseItem {
    endDate: string;
    skuType: ConsumerSkuType;
}

@Injectable()
export class BulkLicensePreviewService extends XhrService implements ConsumerLicenseService {
    private readonly market: string;
    private readonly serviceUrl: string;
    private cvExtension: number;
    private readonly capabilities: CapabilitiesConfig;
    private readonly startupConfig: ClientStartupConfig;
    private readonly ustConfig: UniversalStoreConfig;
    private readonly requestConfig: HttpRequestOptions;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        eventService: EventService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        capabilityService: CapabilitiesService,
        startupConfigService: ClientStartupConfigService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        ustConfigService: UniversalStoreConfigService,
        scheduler: SchedulerService,
        private readonly serviceWorkerMessageService: ServiceWorkerMessageService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.BulkLicensePreview, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        this.capabilities = capabilityService.capabilities;
        this.startupConfig = startupConfigService.config;
        this.ustConfig = ustConfigService.config;

        if (this.startupConfig.authType === AuthType.MSA && this.capabilities.pwaEnabled) {
            this.market = contextService.sharedContext.officeMarket;
            if (this.ustConfig.BulkLicensePreviewApiPath && this.ustConfig.BulkLicensePreviewApiPath) {
                if (this.inMockMode) {
                    this.serviceUrl = `${contextService.sharedContext.officeHomeApiRoot}/${this.ustConfig.BulkLicensePreviewApiPath}`;
                    for (const param in this.queryParams) {
                        if (param && this.queryParams.hasOwnProperty(param) && param.toLowerCase() === 'skutype') {
                            this.serviceUrl += `?skutype=${this.queryParams[param]}`;
                        }
                    }
                } else {
                    this.serviceUrl = `${this.ustConfig.CollectionsApiRoot}/${this.ustConfig.BulkLicensePreviewApiPath}`;
                }
            }

            this.cvExtension = -1;

            //Pre-fetch BulkLicensePreview token for MSA when in pwa app
            this.accessTokenService.getAccessToken(AccessTokenConsumer.BulkLicensePreview);

            //Set up event listener to clear cached license info in service worker if user converted to paid
            eventService.events.withScope(ObservableScope.OnlyFuture)
            .pipe(filter(event => event.name === AngularEvents.convertedToPaid), take(1))
            .toPromise()
            .then(() => {
                this.serviceWorkerMessageService.sendTransactionalMessage<ClearRuntimeCacheMessage>({
                    type: 'runtime-cache',
                    meta: 'delete-cache',
                    payload: {
                        cacheName: 'bulk-license-preview-api'
                    }
                } as ClearRuntimeCacheMessage).then(messageEvent => {
                    const message: ClearRuntimeCacheMessage = messageEvent.data;
                    const payload: RuntimeCachePayload = message.payload;

                    if (!(message && message.type === 'runtime-cache' && message.meta === 'delete-cache' && payload && payload.cacheName === 'bulk-license-preview-api')) {
                        return;
                    }

                    const params: InstrumentationParams = {
                        [InstrumentationParam.State]: payload.params ? (payload.params as any).state : 'Processed',
                        [InstrumentationParam.ElementId]: 'bulk-license-preview-api'
                    };
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.ClearSWCache, params);
                    logFeature({
                        featureName: KnownFeatureNames.Upsell,
                        eventName: KnownInstrumentationEvents.ClearSWCache,
                        area: 'Other',
                        params: {
                            ElementId: 'bulk-license-preview-api',
                            State: payload.params ? (payload.params as any).state : 'Processed'
                        }
                    });
                });
            });
        }

        this.requestConfig = {
            headers: new Map([
                ['content-type', 'application/json'],
                ['MS-CorrelationId', startupConfigService.config.sharedClientStartupContext.correlationId]
            ])
        };
    }

    /*
     * Returns a list of products the user has valid licenses for.
     */
    public getLicenses(): OperationStream<UserLicenses> {
        if (this.startupConfig.authType !== AuthType.MSA || !this.capabilities.pwaEnabled) {
            throw new Error('Not implemented');
        }

        const licensesFromCache = this.fetchLicenses(FetchStrategy.CacheOnly);
        const licensesFromNetwork = this.fetchLicenses(FetchStrategy.NetworkFirst);
        const licensesDataStream = ObservableHelper.createResultsWithCacheObservable(
            licensesFromCache,
            licensesFromNetwork
        );

        return licensesDataStream;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'skutype' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private fetchLicenses(strategy: FetchStrategy): Promise<UserLicenses> {
        return new Promise<UserLicenses>((resolve, reject) => {
            if (!this.serviceUrl) {
                reject(new XhrServiceError(null, 'Failed to get license. Service url is not defined'));
                return;
            }

            this.requestConfig.headers.set('MS-CV', this.startupConfig.sharedClientStartupContext.correlationId + '.' + (++this.cvExtension).toString());
            this.requestConfig.body = this.getBody();

            const callback = (promise: InstrumentedHttpPromise<BulkLicensePreviewData>) => {
                promise
                    .then(result => {
                        if (result.ok && result.data) {
                            resolve(this.convertToUserLicenses(result.data.items, result.fromCache));
                        } else {
                            reject(new XhrServiceError(result, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.zyhCG);
                    });
            };

            const updateRequestBody = ((config: HttpRequestOptions, tokenResponse: AccessTokenResponse): void => {
                if (!config || !tokenResponse) {
                    return;
                }

                const body = config.body as BulkLicensePreviewRequestBody;
                body.beneficiaries[0].identityValue = tokenResponse.token;
            });

            this.post(KnownInstrumentationEvents.BulkLicensePreviewRequest, KnownRequestInstrumentationEvents.BulkLicensePreviewRequest, this.serviceUrl, this.requestConfig.body, callback, reject, this.requestConfig,
                null, null, null, null, AccessTokenConsumer.BulkLicensePreview, null, updateRequestBody, strategy);
        });
    }

    private getBody(token = ''): BulkLicensePreviewRequestBody {
        const userIdentities: Array<UserIdentityContractV8> = [{
            identityType: 'msa',
            identityValue: token,
            localTicketReference: 'OfficeHomeMsa'
        }];

        // Subscription name and products ids are at
        // https://microsoft.sharepoint.com/:w:/r/teams/OfficeLifecyclePerformance/_layouts/15/doc2.aspx?sourcedoc=%7B2f64b299-62cd-4245-ae88-ffc843e135a3%7D&action=edit&uid=%7B2F64B299-62CD-4245-AE88-FFC843E135A3%7D&ListItemId=457&ListId=%7B50ADF55F-0C07-46A4-868B-9199471D575A%7D&odsp=1&env=prodbubble
        // Check if user has any subscription in subscription or perpetual suite
        const productIds: Array<string> = `${this.ustConfig.SubscriptionSuiteIds},${this.ustConfig.PerpetualSuiteIds},${this.ustConfig.PerpetualSuiteIds2019}`.split(',');
        const officeProductIds: Array<ProductSkuIdContractV8> = productIds.map(id => ({productId: id}));

        const requestBody: BulkLicensePreviewRequestBody = {
            beneficiaries: userIdentities,
            market: this.market,
            validityType: LicenseValidityType.All,
            excludeDuplicates: true,
            excludeSharingExpansion: false,
            expandSatisfyingItems: true,
            logFilteredItems: true,
            productSkuIds: officeProductIds
        };

        return requestBody;
    }

    private convertToUserLicenses(licenseItems: Array<LicenseItem>, fromCache = false): UserLicenses {
        if (licenseItems && licenseItems.length === 0) {
            return ({
                currentSkuInfo: {skuType: ConsumerSkuType.NotSubscribed, daysExpired: -1 },
                expiredSkuInfo: {skuType: ConsumerSkuType.NotSubscribed, daysExpired: -1 },
                diagnosticInfo: '',
                fromCache
            });

        }

        let currentSku = ConsumerSkuType.NotDetected;
        let activeDaysLeft = -1;
        let expiredSku = ConsumerSkuType.NotDetected;
        let expiredDays = -1;
        let diagnosticInfo = '';

        if (licenseItems && licenseItems.length > 0) {
            currentSku = ConsumerSkuType.NotSubscribed;
            expiredSku = ConsumerSkuType.NotSubscribed;
            diagnosticInfo = licenseItems.map(i => i.productId + ':' + i.status).join();
            const activeItems = licenseItems.filter(i => i.status.toLowerCase() === LicenseStatus.Active).map(i => this.mapToLightLicenseItem(i)).sort((a, b) => this.sortBySkuOrder(a, b));

            if (activeItems.length > 0) {
                currentSku = activeItems[0].skuType;
                activeDaysLeft = Math.ceil((Date.parse(activeItems[0].endDate) - Date.now()) / (1000 * 3600 * 24));
            }

            const expiredItems = licenseItems.filter(i => i.status.toLowerCase() === LicenseStatus.Expired).sort((a, b) => this.sortByEndDate(a, b));
            if (expiredItems.length > 0) {
                expiredSku = this.getSkuType(expiredItems[0]);
                expiredDays = Math.ceil((Date.now() - Date.parse(expiredItems[0].endDate)) / (1000 * 3600 * 24));
            }
        }

        return {
            currentSkuInfo: { skuType: currentSku, daysExpired: -activeDaysLeft },
            expiredSkuInfo: { skuType: expiredSku, daysExpired: expiredDays },
            diagnosticInfo,
            fromCache
        };
    }

    private mapToLightLicenseItem(item: LicenseItem): LightLicenseItem {
        return {
            endDate: item.endDate,
            skuType: this.getSkuType(item)
        };
    }

    private sortByEndDate(a: LicenseItem, b: LicenseItem): number {
        const dateA = Date.parse(a.endDate) || 0;
        const dateB = Date.parse(b.endDate) || 0;
        return dateA >= dateB ? -1 : 1;
    }

    private sortBySkuOrder(a: LightLicenseItem, b: LightLicenseItem): number {
        return a.skuType <= b.skuType ? -1 : 1;
    }

    /* Return skuType from products user has license for.
     * Business logic:
     * return NotSubscribed if user has no product.
     * iterate through products user has,
     *    if the product is in Subscription suite, return corresponding skuType(O365Personal, O365Home, OtherSubscription).
     *    if the product is in Perpetual suite, set skuType as Perpetual and move to next product.
     *    return NotSubscribed if none of the products Ids is in Subscription and Perpetual suites.
     * Same logic in ConsumerSubscriptionProvider.cs*/
    private getSkuType(licenseItem: LicenseItem): ConsumerSkuType {
        if (!licenseItem.productId) {
            return ConsumerSkuType.NotDetected;
        }

        let skuType = ConsumerSkuType.NotSubscribed;
        const o365PersonalProductId = this.ustConfig.O365PersonalProductId;
        const o365SoloProductId = this.ustConfig.O365SoloProductId;
        const o365HomeProductId = this.ustConfig.O365HomeProductId;
        const subscriptionSuiteIds = this.ustConfig.SubscriptionSuiteIds;
        const perpetualSuiteIds = this.ustConfig.PerpetualSuiteIds;
        const perpetualSuiteIds2019 = this.ustConfig.PerpetualSuiteIds2019;
        const id = licenseItem.productId.toUpperCase();

        if (subscriptionSuiteIds.indexOf(id) >= 0) {
            switch (id) {
                case o365HomeProductId: {
                    if (licenseItem.trialData.isTrial) {
                        skuType = ConsumerSkuType.O365HomeTrial;
                    } else {
                        skuType = ConsumerSkuType.O365Home;
                    }
                    break;
                }
                case o365PersonalProductId: {
                    skuType = ConsumerSkuType.O365Personal;
                    break;
                }
                case o365SoloProductId: {
                    skuType = ConsumerSkuType.O365Solo;
                    break;
                }
                default: {
                    skuType = ConsumerSkuType.OtherSubscription;
                    break;
                }
            }
        } else if (perpetualSuiteIds.indexOf(id) >= 0) {
            skuType = ConsumerSkuType.Perpetual;
        } else if (perpetualSuiteIds2019.indexOf(id) >= 0) {
            skuType = ConsumerSkuType.Perpetual2019;
        }

        return skuType;
    }
}
